/**
 * Order what is missing — Board 3 B and C (`docs/visit-page.md` §8.4) and,
 * while purchasing is not switched on, Board 18 B (§27.3).
 *
 * One sheet (560, from the right edge — Slice 6's `VisitCard`, 562 with its
 * border). When a PO with the same vendor already has a pickup by this
 * visit's crew on the needed morning, the green band says so and **Add to
 * <PO>** comes first; otherwise the fields of *A new order*. With
 * `purchasing.orders` off the band is blue, the PO carries **Noted**, the two
 * optional fields and the price they gave are asked for, and the button notes
 * the order instead of sending it — `PurchasingPort`'s stand-in (§27.1),
 * which writes the same `purchase_orders` / `po_lines` rows.
 *
 * Every figure — the vendors' prices, the estimate, the difference — is the
 * server's: `GET …/order-options?vendorId=&qty=&price=` re-prices what is
 * typed, and the page draws what comes back.
 *
 * `OrderSheet` is exported for the Overview's Ready rows and the phone's
 * *Order · n* (lead decisions, round 3); `OrderToast` is Board 3 C's toast
 * with **Undo**.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import type {
  IsoMoment, MaterialState, OrderAnswer, OrderHow, OrderOptions, PersonRef, PoRef, PoStatus, VendorRef, VisitRead,
  VisitTabKey,
} from '@rowted/shared'
import { ORDER_HOWS } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { visitPath, useVisitWrite, type VisitWriteResult } from './api.js'
import { VisitButton, VisitCard, VISIT_LAYER } from './sheet.js'
import { VisitGlyph } from './glyphs.js'
import { Face } from './face.js'
import { COMMON, ORDER, RENTALS } from './copy.js'
import { dayText, money, signedMoney, timeIn } from './format.js'
import {
  MaterialChip, NotedTag, count,
} from './materials-parts.js'
import { ChoiceChip, FIELD_BOX, FIELD_INPUT, FieldLabel, RadioDot } from './fields.js'
import { InternalChip } from './internal-chip.js'

/** The day a moment falls on in the visit's zone — so a weekday can be named (*Friday's pickup*). */
function dayOf(timeZone: string, iso: string): string {
  return formatInTimeZone(new Date(iso), timeZone, 'yyyy-MM-dd')
}

/** A PO's state as the line it carries would stand (§8.2): placed → Ordered, ready → Ready for pickup … */
function poChip(status: PoStatus): MaterialState {
  if (status === 'ready_for_pickup') return 'ready_pickup'
  if (status === 'delivered' || status === 'picked_up' || status === 'receipt_verified' || status === 'synced') return 'on_site'
  return 'ordered'
}

type Mode = 'add' | 'new'

/**
 * A PO an earlier sheet of *Order what's needed* wrote or joined for its
 * vendor (§8.1, lead decision round 7): a later line of that vendor's opens on
 * **Add to <PO>** when the PO comes in time for it, so a vendor ends with one
 * order per trip.
 */
export interface OrderRunPo {
  po: PoRef
  vendor: VendorRef
  how: OrderHow
  picker: PersonRef | null
  /** When it comes: the pickup's time, or a delivery's *Needed*. */
  arrives: IsoMoment | null
  noted: { by: PersonRef; at: IsoMoment } | null
}

/** What **Add to <PO>** adds to — the server's same trip (Board 3 B), or this run's PO for the vendor. */
interface Join {
  po: PoRef
  vendor: VendorRef
  how: OrderHow
  picker: PersonRef | null
  pickupAt: IsoMoment | null
  noted: { by: PersonRef; at: IsoMoment } | null
}

/** The run's PO for the line's vendor, when it comes at or before the line is needed. */
function runPoFor(run: readonly OrderRunPo[], options: OrderOptions): OrderRunPo | null {
  const vendorId = options.vendorId
  if (!vendorId) return null
  const found = run.find((entry) => entry.vendor.id === vendorId)
  if (!found) return null
  const needed = options.neededAt ?? options.line.neededBy
  if (needed && found.arrives && Date.parse(found.arrives) > Date.parse(needed)) return null
  return found
}

export function OrderSheet({ read, lineId, phone, tab = 'materials', run = [], onClose, onDone }: {
  read: VisitRead
  lineId: string
  /** *Order what's needed*: the POs this run's earlier sheets wrote, one per vendor. */
  run?: readonly OrderRunPo[]
  phone: boolean
  /** The tab whose fresh read the write asks for (`overview` from the Ready rows). */
  tab?: VisitTabKey
  onClose: () => void
  /** `joined` is what this sheet ordered on, for the run's later sheets. */
  onDone: (result: VisitWriteResult, ordered: OrderAnswer['ordered'] | null, joined: OrderRunPo | null) => void
}) {
  const tz = read.timeZone
  const noted = !read.capabilities['purchasing.orders']
  const write = useVisitWrite(read.id)
  const [options, setOptions] = useState<OrderOptions | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /* What is typed. Undefined means "as the server proposed". */
  const [mode, setMode] = useState<Mode | null>(null)
  const [vendorId, setVendorId] = useState<string | null>(null)
  const [qty, setQty] = useState<string>('')
  const [how, setHow] = useState<OrderHow | null>(null)
  const [picker, setPicker] = useState<PersonRef | null>(null)
  const [pickupAt, setPickupAt] = useState<string | null>(null)
  const [neededAt, setNeededAt] = useState<string | null>(null)
  const [vendorContact, setVendorContact] = useState('')
  const [vendorRef, setVendorRef] = useState('')
  const [price, setPrice] = useState('')
  /* The price they gave reads as money (*$689.00*) until it is being typed. */
  const [priceFocused, setPriceFocused] = useState(false)
  const seeded = useRef(false)
  /* The run's PO for this line's vendor, chosen once, as the sheet opens — the run as it stood then. */
  const runAtOpen = useRef(run)
  const [runPo, setRunPo] = useState<OrderRunPo | null>(null)

  /* The options, re-priced for the vendor, the quantity and the price typed — debounced, the latest answer wins. */
  const query = useMemo(() => {
    const params = new URLSearchParams()
    if (vendorId) params.set('vendorId', vendorId)
    if (qty && Number(qty) > 0) params.set('qty', qty)
    if (noted && /^\d+(\.\d{1,2})?$/.test(price)) params.set('price', price)
    return params.toString()
  }, [vendorId, qty, price, noted])
  const latest = useRef(0)
  useEffect(() => {
    const ticket = ++latest.current
    const timer = window.setTimeout(() => {
      const path = visitPath(read.id, `/materials/${encodeURIComponent(lineId)}/order-options`)
      request<OrderOptions>(query ? `${path}?${query}` : path)
        .then((body) => {
          if (ticket !== latest.current) return
          setOptions(body)
          if (!seeded.current) {
            seeded.current = true
            const fromRun = body.sameTrip ? null : runPoFor(runAtOpen.current, body)
            setRunPo(fromRun)
            setMode(body.sameTrip || fromRun ? 'add' : 'new')
            setQty(String(body.qty))
            if (noted && body.internal) setPrice(body.internal.thisPrice)
          }
        })
        .catch((cause: unknown) => { if (ticket === latest.current) setError(cause instanceof Error ? cause.message : String(cause)) })
    }, seeded.current ? 200 : 0)
    return () => window.clearTimeout(timer)
  }, [read.id, lineId, query, noted])

  const sameTrip = options?.sameTrip ?? null
  const trip: Join | null = sameTrip
    ? { po: sameTrip.po, vendor: sameTrip.vendor, how: 'pickup', picker: sameTrip.picker, pickupAt: sameTrip.pickupAt, noted: sameTrip.noted }
    : runPo
      ? {
        po: runPo.po,
        vendor: runPo.vendor,
        how: runPo.how,
        picker: runPo.how === 'pickup' ? runPo.picker : null,
        pickupAt: runPo.how === 'pickup' ? runPo.arrives : null,
        noted: runPo.noted,
      }
      : null
  /* Board 3 B's band and the choice's sub are a pickup's words; a run's delivery PO draws the choice and its chip only (lead, round 7). */
  const tripPickup = trip && trip.how === 'pickup' && trip.picker && trip.pickupAt
    ? { picker: trip.picker, pickupAt: trip.pickupAt }
    : null
  const adding = mode === 'add' && trip !== null
  /* With Add to <PO> chosen, the vendor, the way and the pickup are that PO's (§8.4). */
  const shownVendorId = adding ? trip.vendor.id : vendorId ?? options?.vendorId ?? null
  const shownHow: OrderHow = adding ? trip.how : how ?? options?.how ?? 'pickup'
  const shownPicker = adding ? trip.picker : picker ?? options?.picker ?? null
  const shownPickupAt = adding ? trip.pickupAt : pickupAt ?? options?.pickupAt ?? null
  const shownNeededAt = neededAt ?? options?.neededAt ?? null

  /** Changing anything Add to <PO> fixed switches the choice to A new order (§8.4). */
  const toNew = () => { if (mode === 'add') setMode('new') }

  async function submit() {
    if (!options) return
    const amount = Number(qty)
    if (!(amount > 0)) return
    const notedBody = noted
      ? { noted: { vendorContact: vendorContact.trim(), vendorRef: vendorRef.trim(), ...(/^\d+(\.\d{1,2})?$/.test(price) ? { price } : {}) } }
      : {}
    const body = adding
      ? { mode: 'add', version: options.line.version, poId: trip.po.id, qty: amount, ...notedBody }
      : {
        mode: 'new',
        version: options.line.version,
        vendorId: shownVendorId,
        how: shownHow,
        neededAt: shownNeededAt,
        ...(shownHow === 'pickup' ? { pickupUserId: shownPicker?.id ?? null, pickupAt: shownPickupAt } : {}),
        qty: amount,
        ...notedBody,
      }
    setBusy(true)
    setError(null)
    try {
      const result = await write('POST', `/materials/${encodeURIComponent(lineId)}/order`, body, { tab })
      const ordered = (result.answer as OrderAnswer | undefined)?.ordered ?? null
      const joined: OrderRunPo | null = !ordered ? null : adding
        ? {
          po: ordered.po,
          vendor: ordered.vendor,
          how: trip.how,
          picker: trip.picker,
          arrives: trip.how === 'pickup' ? trip.pickupAt : runPo?.arrives ?? shownNeededAt,
          noted: trip.noted,
        }
        : {
          po: ordered.po,
          vendor: ordered.vendor,
          how: shownHow,
          picker: shownHow === 'pickup' ? shownPicker : null,
          arrives: shownHow === 'pickup' ? shownPickupAt : shownNeededAt,
          noted: noted ? { by: options.you, at: new Date().toISOString() } : null,
        }
      onDone(result, ordered, joined)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const line = options?.line
  const title = line ? ORDER.title(line.names.name) : ''
  const sub = line && line.neededBy
    ? ORDER.sub(read.reference, String(line.day), dayText(dayOf(tz, line.neededBy), 'date'), timeIn(tz, line.neededBy, 'clock'))
    : undefined
  const noun = line?.names.noun ?? ''
  const pickupWeekday = shownPickupAt ? dayText(dayOf(tz, shownPickupAt), 'weekday') : ''

  const note = (() => {
    if (!options || shownHow !== 'pickup' || !shownPicker) return undefined
    if (noted) return adding ? ORDER.noted.footer(trip.po.number, options.you.name, noun, pickupWeekday, shownPicker.firstName) : undefined
    return options.buyer ? ORDER.footer(options.buyer.name, noun, pickupWeekday, shownPicker.firstName) : undefined
  })()

  const primary = noted
    ? (adding ? ORDER.noted.noteItOn(trip.po.number) : ORDER.noted.noteTheOrder)
    : (adding ? ORDER.addTo(trip.po.number) : ORDER.placeOrder)

  return (
    <VisitCard
      width={562}
      phone={phone}
      title={title}
      {...(sub ? { sub } : {})}
      mark={(
        <span className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] border border-warn-line bg-warn-bg text-warn">
          <VisitGlyph name="cart" size={18} stroke={1.9} />
        </span>
      )}
      onClose={onClose}
      layer={VISIT_LAYER}
      asDrawn={noted ? 'vp-18 B' : 'vp-03 B'}
      {...(note ? { note } : {})}
      footer={(
        <>
          <VisitButton size={phone ? 'phone' : 'card'} onClick={onClose}>{ORDER.cancel}</VisitButton>
          <VisitButton
            size={phone ? 'phone' : 'card'}
            tone="primary"
            glyph="check"
            disabled={busy || !options || !(Number(qty) > 0) || (!adding && !shownVendorId)}
            onClick={() => void submit()}
          >
            {primary}
          </VisitButton>
        </>
      )}
    >
      {options && line ? (
        <div className="flex flex-col gap-3.5">
          {noted ? (
            <div className="flex items-start gap-[9px] rounded-[10px] border border-info-line bg-info-bg px-3 py-2.5
                            text-[12.5px] leading-[1.45] text-info">
              <VisitGlyph name="info" size={15} stroke={2} />
              <div>
                <b>{ORDER.noted.bandTitle}</b>{' '}
                {ORDER.noted.band(options.vendors.find((row) => row.vendor.id === shownVendorId)?.vendor.name ?? trip?.vendor.name ?? '')}
              </div>
            </div>
          ) : trip && tripPickup && (
            <div className="flex items-start gap-[9px] rounded-[10px] border border-accent-line bg-accent-soft px-3 py-2.5
                            text-[12.5px] leading-[1.45] text-accent-dark">
              <VisitGlyph name="sparkle" size={15} stroke={2} />
              <div>
                <b>{ORDER.stopping(tripPickup.picker.firstName, trip.vendor.name)}</b>{' '}
                {ORDER.stoppingSub(timeIn(tz, tripPickup.pickupAt, 'day'), timeIn(tz, tripPickup.pickupAt, 'clock'), trip.po.number, noun)}
              </div>
            </div>
          )}

          {trip && (
            <div role="radiogroup" className="flex flex-col gap-2">
              <ChoiceBox on={adding} onPick={() => setMode('add')}>
                <div className="flex min-w-0 flex-1 flex-col">
                  <b className="text-[13px]">{ORDER.addTo(trip.po.number)}</b>
                  {tripPickup && (
                    <span className="mt-0.5 text-[11.5px] leading-[1.4] text-text-muted">
                      {trip.noted
                        ? ORDER.addToSubNoted(trip.vendor.name, tripPickup.picker.name, timeIn(tz, tripPickup.pickupAt, 'dayClock'),
                          timeIn(tz, trip.noted.at, 'day'), trip.noted.by.name)
                        : ORDER.addToSub(trip.vendor.name, tripPickup.picker.name, timeIn(tz, tripPickup.pickupAt, 'dayClock'))}
                    </span>
                  )}
                </div>
                <span className="flex items-center gap-1.5">
                  {trip.po.entry === 'noted' && <NotedTag />}
                  <MaterialChip state={poChip(trip.po.status)} />
                </span>
              </ChoiceBox>
              <ChoiceBox on={!adding} onPick={() => setMode('new')}>
                <div className="flex min-w-0 flex-1 flex-col">
                  <b className="text-[13px]">{ORDER.newOrder}</b>
                  <span className="mt-0.5 text-[11.5px] leading-[1.4] text-text-muted">{ORDER.newOrderSub}</span>
                </div>
              </ChoiceBox>
            </div>
          )}

          <div className="min-w-0">
            <FieldLabel>{ORDER.vendor}</FieldLabel>
            <div role="radiogroup" aria-label={ORDER.vendor} className="overflow-hidden rounded-[10px] border border-line">
              {options.vendors.map((row, index) => {
                const on = row.vendor.id === shownVendorId
                const address = row.vendor.line1 && row.vendor.city
                  ? RENTALS.address(row.vendor.line1, row.vendor.city)
                  : row.vendor.line1 ?? row.vendor.city ?? ''
                const where = row.driveFromYard === null
                  ? address
                  : index === 0 ? ORDER.fromYard(address, count(row.driveFromYard)) : ORDER.fromYardShort(address, count(row.driveFromYard))
                return (
                  <button
                    key={row.vendor.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => { setVendorId(row.vendor.id); if (trip && row.vendor.id !== trip.vendor.id) toNew() }}
                    className={cn('flex w-full items-center gap-2.5 border-t border-line-faint px-3 py-[9px] text-left',
                      on ? 'bg-green-band-bg' : 'bg-surface hover:bg-hover')}
                  >
                    <RadioDot on={on} size={16} />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <b className="text-[12.5px]">{row.vendor.name}</b>
                      <span className="text-[11.5px] text-text-muted">{where}</span>
                    </div>
                    <div className="flex flex-col">
                      {row.internal?.unitPrice && <b className="text-right text-[12.5px]">{money(row.internal.unitPrice)}</b>}
                      {row.stock !== null && (
                        <span className="text-right text-[11px] font-semibold text-accent-dark">{ORDER.inStock(count(row.stock))}</span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
            <div className="min-w-0">
              <FieldLabel htmlFor="order-qty">{ORDER.quantity}</FieldLabel>
              <div className={FIELD_BOX}>
                <input
                  id="order-qty"
                  inputMode="decimal"
                  value={qty}
                  onChange={(event) => setQty(event.target.value)}
                  className={FIELD_INPUT}
                />
                <span className="ml-1.5 text-[12px] text-text-faint">{line.unit ? (Number(qty) === 1 ? line.unit.one : line.unit.many) : noun}</span>
              </div>
            </div>
            <div className="min-w-0">
              <FieldLabel>{ORDER.needed}</FieldLabel>
              <MomentField
                timeZone={tz}
                value={shownNeededAt}
                words={(at) => ORDER.neededBy(dayText(dayOf(tz, at), 'date'), timeIn(tz, at, 'ampm'))}
                onChange={setNeededAt}
              />
            </div>
          </div>

          <div className="min-w-0">
            <FieldLabel>{ORDER.howItGetsThere}</FieldLabel>
            <div role="radiogroup" aria-label={ORDER.howItGetsThere} className="flex flex-wrap gap-1.5">
              {ORDER_HOWS.map((key) => (
                <ChoiceChip key={key} size={30} on={shownHow === key} onClick={() => { setHow(key); if (trip && key !== trip.how) toNew() }}>
                  {ORDER.how[key]}
                </ChoiceChip>
              ))}
            </div>
          </div>

          {shownHow === 'pickup' && (
            <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
              <div className="min-w-0">
                <FieldLabel>{ORDER.picksUp}</FieldLabel>
                <PersonField
                  people={options.pickers}
                  value={shownPicker}
                  onChange={(person) => { setPicker(person); if (trip && person.id !== trip.picker?.id) toNew() }}
                />
              </div>
              <div className="min-w-0">
                <FieldLabel>{ORDER.when}</FieldLabel>
                <MomentField
                  timeZone={tz}
                  value={shownPickupAt}
                  words={(at) => COMMON.dot(dayText(dayOf(tz, at), 'date'), timeIn(tz, at, 'ampm'))}
                  onChange={(at) => { setPickupAt(at); toNew() }}
                />
              </div>
            </div>
          )}

          {noted && (
            <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
              <div className="min-w-0">
                <FieldLabel htmlFor="order-contact" optional={ORDER.noted.optional}>{ORDER.noted.whoYouSpokeTo}</FieldLabel>
                <div className={FIELD_BOX}>
                  <input id="order-contact" value={vendorContact} onChange={(event) => setVendorContact(event.target.value)} className={FIELD_INPUT} />
                </div>
              </div>
              <div className="min-w-0">
                <FieldLabel htmlFor="order-ref" optional={ORDER.noted.optional}>{ORDER.noted.theirOrderNumber}</FieldLabel>
                <div className={FIELD_BOX}>
                  <input id="order-ref" value={vendorRef} onChange={(event) => setVendorRef(event.target.value)} className={FIELD_INPUT} />
                </div>
              </div>
            </div>
          )}

          {options.internal && (
            <div className="flex items-center gap-2.5 rounded-[10px] border border-line bg-surface-internal px-3 py-2.5 text-[12.5px] text-text-body">
              <InternalChip />
              {noted ? (
                <>
                  <span>{ORDER.noted.priceLine(money(options.internal.estimate))}</span>
                  <span className="flex h-[30px] w-[104px] items-center rounded-button border border-line bg-surface px-[11px] text-[12.5px] text-text focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]">
                    <input
                      inputMode="decimal"
                      aria-label={ORDER.noted.priceLine(money(options.internal.estimate))}
                      value={priceFocused || !/^\d+(\.\d{1,2})?$/.test(price) ? price : money(price)}
                      onFocus={() => setPriceFocused(true)}
                      onBlur={() => setPriceFocused(false)}
                      onChange={(event) => setPrice(event.target.value.replace(/[^\d.]/g, ''))}
                      className={FIELD_INPUT}
                    />
                  </span>
                </>
              ) : (
                <span>{ORDER.priceLine(money(options.internal.estimate), money(options.internal.thisPrice))}</span>
              )}
              <span className="flex-1" />
              {options.internal.delta.tone === 'zero'
                ? <b className="text-text-faint">{ORDER.noDifference}</b>
                : <b className={options.internal.delta.tone === 'over' ? 'text-warn' : 'text-accent-dark'}>{signedMoney(options.internal.delta)}</b>}
            </div>
          )}

          {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
        </div>
      ) : error ? <p className="m-0 text-[12.5px] text-danger">{error}</p> : null}
    </VisitCard>
  )
}

/** Board 3 B's radio card — 18 px radio, 11 radius, the green ring when chosen. */
function ChoiceBox({ on, onPick, children }: { on: boolean; onPick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onPick}
      className={cn(
        'flex items-start gap-[11px] rounded-[11px] border-[1.5px] px-3 py-[11px] text-left',
        on ? 'border-accent bg-green-band-bg shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]' : 'border-line bg-surface hover:bg-hover',
      )}
    >
      <RadioDot on={on} size={18} />
      {children}
    </button>
  )
}

/**
 * A moment as the board prints it in its 36 px box (*Fri Oct 23 · 6:45 AM*);
 * pressed, the same box takes a date and time in the visit's own zone.
 */
function MomentField({ timeZone, value, words, onChange }: {
  timeZone: string
  value: string | null
  words: (at: string) => string
  onChange: (at: string) => void
}) {
  const [editing, setEditing] = useState(false)
  if (editing || !value) {
    return (
      <div className={FIELD_BOX}>
        <input
          type="datetime-local"
          autoFocus={editing}
          defaultValue={value ? formatInTimeZone(new Date(value), timeZone, "yyyy-MM-dd'T'HH:mm") : ''}
          onBlur={() => setEditing(false)}
          onChange={(event) => {
            if (event.target.value) onChange(fromZonedTime(event.target.value, timeZone).toISOString())
          }}
          className={FIELD_INPUT}
        />
      </div>
    )
  }
  return (
    <button type="button" onClick={() => setEditing(true)} className={cn(FIELD_BOX, 'text-left')}>
      <span className="min-w-0 flex-1 truncate">{words(value)}</span>
    </button>
  )
}

/** *Picks up* — a person on the day's crew: their face, their name, the chevron; pressed, the crew to pick from. */
function PersonField({ people, value, onChange }: {
  people: PersonRef[]
  value: PersonRef | null
  onChange: (person: PersonRef) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className={cn(FIELD_BOX, 'gap-2 text-left')}
      >
        {value && <Face person={value} size={22} />}
        <span className="min-w-0 flex-1 truncate">{value?.name ?? ''}</span>
        <span className="inline-flex text-text-muted"><VisitGlyph name="chevronDown" size={13} stroke={2} /></span>
      </button>
      {open && (
        <div role="listbox" className="absolute inset-x-0 top-[calc(100%+4px)] z-10 overflow-hidden rounded-[10px] border border-line bg-surface shadow-popover">
          {people.map((person) => (
            <button
              key={person.id}
              type="button"
              role="option"
              aria-selected={person.id === value?.id}
              onClick={() => { onChange(person); setOpen(false) }}
              className={cn('flex min-h-9 w-full items-center gap-2 px-[11px] text-left text-[13px] hover:bg-hover',
                person.id === value?.id && 'bg-green-band-bg')}
            >
              <Face person={person} size={22} />
              {person.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Board 3 C — the white toast, with Undo
// ---------------------------------------------------------------------------

/**
 * *Added to PO-1051 — Capital Hardscape Supply has the updated order* ·
 * **Undo**: removes the PO line and re-sends the PO (§7.9). It goes after a
 * while on its own.
 */
export function OrderToast({ read, ordered, tab = 'materials', phone, onUndone, onClose }: {
  read: VisitRead
  ordered: OrderAnswer['ordered']
  tab?: VisitTabKey
  phone: boolean
  onUndone: (result: VisitWriteResult) => void
  onClose: () => void
}) {
  const write = useVisitWrite(read.id)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(onClose, 10_000)
    return () => window.clearTimeout(timer)
  }, [onClose])

  async function undo() {
    setBusy(true)
    try {
      const result = await write('POST', `/materials/${encodeURIComponent(ordered.lineId)}/order/remove`, { version: ordered.lineVersion }, { tab })
      onUndone(result)
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="status"
      data-as-drawn="vp-03 C toast"
      className={cn(
        'fixed z-[70] flex items-center gap-2.5 rounded-[12px] border border-line bg-surface px-3.5 py-2.5 text-[12.5px] text-text',
        'shadow-[0_10px_30px_rgba(20,20,10,0.12)]',
        phone ? 'inset-x-4 bottom-[88px]' : 'bottom-6 left-1/2 w-[520px] -translate-x-1/2',
      )}
    >
      <span className="inline-flex text-accent"><VisitGlyph name="checkCircle" size={16} stroke={2} /></span>
      <span className="min-w-0">{ORDER.added(ordered.po.number, ordered.vendor.name)}</span>
      <span className="flex-1" />
      <button type="button" disabled={busy} onClick={() => void undo()} className="font-bold text-accent hover:text-accent-dark">
        {ORDER.undo}
      </button>
    </div>
  )
}
